import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { CheckCircle, Star, ArrowBack } from '@mui/icons-material';
import { subscriptionService } from '../services/subscriptionService';

const SubscriptionModal = ({ open, onClose, onSuccess, currentSubscription = null }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [paymentData, setPaymentData] = useState({
    dateTransaction: new Date().toISOString().split('T')[0],
    numeroTelephone: '',
    referenceTransaction: '',
    mode: '',
    montant: ''
  });

  useEffect(() => {
    if (open) {
      loadPlans();
      setError('');
      setSelectedPlan(null);
      setActiveStep(0);
      setPaymentData({
        dateTransaction: new Date().toISOString().split('T')[0],
        numeroTelephone: '',
        referenceTransaction: '',
        mode: '',
        montant: ''
      });
    }
  }, [open]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.getSubscriptionPlans({ statut: true });
      // console.log('Réponse des formules:', response);
      // Filtrer la formule FREE si elle existe
      // La réponse peut être { success: true, data: [...] } ou directement un tableau
      let activePlans = [];
      if (Array.isArray(response)) {
        activePlans = response;
      } else if (response && Array.isArray(response.data)) {
        activePlans = response.data;
      } else if (response && response.success && Array.isArray(response.data)) {
        activePlans = response.data;
      }
      
      activePlans = activePlans.filter(plan => plan.code !== 'FREE' && plan.code !== 'FR');
      
      // Si un abonnement actif existe, filtrer pour ne montrer que les plans supérieurs ou égaux
      if (currentSubscription && currentSubscription.statut === 'actif' && currentSubscription.formule) {
        const currentPlanNombreProduit = currentSubscription.formule.nombreProduit || 0;
        activePlans = activePlans.filter(plan => {
          const planNombreProduit = plan.nombreProduit || 0;
          // Permettre les plans avec nombreProduit >= au plan actuel
          return planNombreProduit >= currentPlanNombreProduit;
        });
      }
      
      // console.log('Plans actifs filtrés:', activePlans);
      setPlans(activePlans);
    } catch (err) {
      console.error('Erreur lors du chargement des formules:', err);
      setError('Erreur lors du chargement des formules d\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (!selectedPlan) {
      setError('Veuillez sélectionner une formule');
      return;
    }
    setError('');
    setActiveStep(1);
    // Pré-remplir le montant avec le prix de la formule
    if (selectedPlan.prix) {
      setPaymentData(prev => ({
        ...prev,
        montant: selectedPlan.prix.toString()
      }));
    }
  };

  const handleBack = () => {
    setError('');
    setActiveStep(0);
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePaymentData = () => {
    if (!paymentData.dateTransaction) {
      setError('La date de transaction est obligatoire');
      return false;
    }
    if (!paymentData.numeroTelephone) {
      setError('Le numéro de téléphone est obligatoire');
      return false;
    }
    if (!paymentData.referenceTransaction) {
      setError('La référence de transaction est obligatoire');
      return false;
    }
    if (!/^\d{11}$/.test(paymentData.referenceTransaction)) {
      setError('La référence de transaction doit contenir exactement 11 chiffres');
      return false;
    }
    if (!paymentData.mode) {
      setError('Le mode de paiement est obligatoire');
      return false;
    }
    if (!paymentData.montant || parseFloat(paymentData.montant) <= 0) {
      setError('Le montant doit être supérieur à 0');
      return false;
    }
    return true;
  };

  const handleSubscribe = async () => {
    if (!validatePaymentData()) {
      return;
    }

    try {
      setSubscribing(true);
      setError('');
      
      if (!selectedPlan || !selectedPlan.id) {
        setError('Plan invalide. Veuillez sélectionner une formule.');
        setSubscribing(false);
        return;
      }
      
      const subscriptionData = {
        formuleId: selectedPlan.id,
        dateTransaction: paymentData.dateTransaction,
        numeroTelephone: paymentData.numeroTelephone,
        referenceTransaction: paymentData.referenceTransaction,
        mode: paymentData.mode,
        montant: parseFloat(paymentData.montant)
      };

      const response = await subscriptionService.subscribeToPlan(subscriptionData);
      
      if (onSuccess) {
        const message = response.message || `Votre demande d'abonnement à la formule "${selectedPlan.libelle}" a été créée avec succès. Elle est en attente de validation par un administrateur.`;
        onSuccess(message);
      }
      onClose();
    } catch (err) {
      console.error('Erreur lors de la souscription:', err);
      console.error('Détails de l\'erreur:', err.response?.data);
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Erreur lors de la souscription. Veuillez réessayer.';
      setError(errorMessage);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Empêcher la fermeture en cliquant en dehors ou en appuyant sur Escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
          Souscrire à une formule d'abonnement
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {activeStep === 0 
            ? 'Choisissez la formule qui correspond le mieux à vos besoins'
            : 'Renseignez les informations de paiement'}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep}>
            <Step>
              <StepLabel>Choix de la formule</StepLabel>
            </Step>
            <Step>
              <StepLabel>Informations de paiement</StepLabel>
            </Step>
          </Stepper>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {currentSubscription && activeStep === 0 && (
          <Alert 
            severity={currentSubscription.statut === 'actif' ? 'success' : 'warning'} 
            sx={{ mt: 3 }}
          >
            <Typography variant="body2">
              <strong>Abonnement {currentSubscription.statut === 'actif' ? 'actif' : 'en attente'}:</strong> {currentSubscription.formule?.libelle || currentSubscription.formule?.intitule || 'N/A'}
              {currentSubscription.statut === 'actif' && currentSubscription.dateFin && (
                <> - Expire le: {new Date(currentSubscription.dateFin).toLocaleDateString('fr-FR')}</>
              )}
              {currentSubscription.statut === 'inactif' && !currentSubscription.dateDebut && (
                <> - En attente de validation par un administrateur</>
              )}
            </Typography>
          </Alert>
        )}

        {activeStep === 0 ? (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : plans.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Aucune formule d'abonnement disponible pour le moment.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 3, 
                mt: 1,
                justifyContent: { xs: 'center', md: 'flex-start' }
              }}>
                {plans.map((plan) => (
                  <Card
                    key={plan.id}
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: selectedPlan?.id === plan.id ? 2 : 1,
                      borderColor: selectedPlan?.id === plan.id ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      },
                      position: 'relative'
                    }}
                    onClick={() => setSelectedPlan(plan)}
                  >
                      {plan.populaire && (
                        <Chip
                          icon={<Star />}
                          label="Populaire"
                          color="primary"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            fontWeight: 600
                          }}
                        />
                      )}
                      
                      <CardContent sx={{ flexGrow: 1, pt: plan.populaire ? 6 : 3 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                          {plan.libelle || plan.intitule || 'Formule'} {'- '}
                          {plan.prix !== undefined && plan.prix !== null && plan.prix > 0
                              ? `${parseFloat(plan.prix).toLocaleString('fr-FR')} FCFA / mois`
                              : 'Sur demande'}
                        </Typography>
                        
                        {plan.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: 40 }}>
                            {plan.description}
                          </Typography>
                        )}
                        
                        <Stack spacing={1.5} sx={{ mb: 1 }}>
                          {plan.nombreProduit !== undefined && plan.nombreProduit !== null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2">
                                {plan.nombreProduit === 0 
                                  ? 'Produits illimités' 
                                  : `${plan.nombreProduit} produit${plan.nombreProduit > 1 ? 's' : ''}`}
                              </Typography>
                            </Box>
                          )}
                          
                          {plan.code && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                              <Typography variant="body2">
                                Code: {plan.code}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            {selectedPlan && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Formule sélectionnée:</strong> {selectedPlan.libelle} - {selectedPlan.prix ? `${parseFloat(selectedPlan.prix).toLocaleString('fr-FR')} FCFA` : 'Sur demande'}
                </Typography>
              </Alert>
            )}
            <Stack spacing={3}>
              <TextField
                name="dateTransaction"
                label="Date de transaction"
                type="date"
                value={paymentData.dateTransaction}
                onChange={handlePaymentChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                name="numeroTelephone"
                label="Numéro de téléphone"
                value={paymentData.numeroTelephone}
                onChange={handlePaymentChange}
                required
                fullWidth
                placeholder="Ex: 90123456"
              />
              <TextField
                name="referenceTransaction"
                label="Référence de transaction"
                value={paymentData.referenceTransaction}
                onChange={handlePaymentChange}
                required
                fullWidth
                placeholder="11 chiffres"
                inputProps={{ maxLength: 11, pattern: '[0-9]*' }}
                helperText="La référence doit contenir exactement 11 chiffres"
              />
              <FormControl fullWidth required>
                <InputLabel>Mode de paiement</InputLabel>
                <Select
                  name="mode"
                  value={paymentData.mode}
                  onChange={handlePaymentChange}
                  label="Mode de paiement"
                >
                  <MenuItem value="Tmoney">Tmoney</MenuItem>
                  <MenuItem value="Flooz">Flooz</MenuItem>
                </Select>
              </FormControl>
              <TextField
                name="montant"
                label="Montant (FCFA)"
                type="number"
                value={paymentData.montant}
                onChange={handlePaymentChange}
                required
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Stack>
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={subscribing}>
          Annuler
        </Button>
        {activeStep === 1 && (
          <Button
            onClick={handleBack}
            disabled={subscribing}
            startIcon={<ArrowBack />}
          >
            Retour
          </Button>
        )}
        {activeStep === 0 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!selectedPlan || subscribing}
          >
            Suivant
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubscribe}
            disabled={subscribing}
            startIcon={subscribing ? <CircularProgress size={20} /> : null}
          >
            {subscribing ? 'Souscription en cours...' : 'Souscrire'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionModal;

